import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts'
import { Box, Typography } from '@mui/material'
import { useStore } from '~/state/useStore'
import { formatAxisTick, formatCompactNumber } from '~/util/numbers'
import type { TimeframeBarData } from './toTimeframeBars'
import { renderHorizontalBarValueLabel } from './chrome/barValueLabel'
import { MESSAGES } from './chrome/chartChrome'
import { AXIS_TICK, GRID_STROKE, TOOLTIP_NUM_STYLE } from './chrome/chartStyle'

interface Props extends TimeframeBarData {
  title: string
  /** Render-only: maps raw series key -> display label for the legend.
   *  Keys stay raw (V1 invariant); only the visible legend name is translated. */
  seriesLabelFor?: (key: string) => string
}

export function TimeframeBarChart({ title, data, series, seriesLabelFor }: Props) {
  const palette = useStore((s) => s.personal.chipPalette)

  if (data.length === 0 || series.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography color="text.secondary">{MESSAGES.noDataForFilters}</Typography>
      </Box>
    )
  }

  const ungrouped = series.length === 1 && series[0] === 'total'

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" align="center">
        {title}
      </Typography>
      <Box sx={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 16, left: 24, right: 40, bottom: 8 }}
          >
            <CartesianGrid stroke={GRID_STROKE} horizontal={false} />
            <XAxis type="number" tickFormatter={formatAxisTick} tick={AXIS_TICK} />
            <YAxis type="category" dataKey="timeframe" width={90} interval={0} tick={AXIS_TICK} />
            <Tooltip
              formatter={(v) => (typeof v === 'number' ? formatCompactNumber(v) : String(v))}
              contentStyle={TOOLTIP_NUM_STYLE}
            />
            {!ungrouped ? <Legend /> : null}
            {series.map((key, si) => (
              <Bar
                key={key}
                dataKey={key}
                name={ungrouped ? title : (seriesLabelFor?.(key) ?? key)}
                fill={palette[si % palette.length] ?? '#5b9cff'}
              >
                {ungrouped
                  ? data.map((_, di) => (
                      <Cell key={di} fill={palette[di % palette.length] ?? '#5b9cff'} />
                    ))
                  : null}
                {/* Row 7 (D3): in-bar value labels; suppression by min-size — see barValueLabel.tsx */}
                <LabelList
                  dataKey={key}
                  content={(p) =>
                    renderHorizontalBarValueLabel(
                      p as Parameters<typeof renderHorizontalBarValueLabel>[0],
                      formatCompactNumber,
                    )
                  }
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}
